import React from 'react';
import { SituationCard } from '../kit';
import { TONE_CLASS } from '../metaTone';

/**
 * One ONE-LINE list row inside a card: a meaning-tinted rail on a dark inset row, the
 * title bold on the left and the meta in muted mono on the right. Work and Requests
 * share it so the two cards cannot drift apart. Longer text (a stalled reason) is not a
 * second line: pass it as `hint` and it becomes the row's tooltip.
 *
 * @param {object} props
 * @param {string} props.rail          Meaning tint of the rail (warm | warn | waiting | neutral).
 * @param {string} props.title
 * @param {string|Array<{text: string, tone?: string|null}>} [props.meta]  Right-hand detail; omitted when empty. An
 *   array draws each part in its tone's colour (see metaTone), joined by " · ".
 * @param {string} [props.hint]        Tooltip (`title` attribute) carrying the fuller text.
 * @param {string} props.testId
 */
export default function RailRow({ rail, title, meta = '', hint = undefined, testId }) {
  const parts = Array.isArray(meta) ? meta.filter((p) => p && p.text) : null;
  const metaText = parts ? parts.map((p) => p.text).join(' · ') : meta;
  return (
    <SituationCard rail={rail} className="!py-3" data-testid={testId} title={hint || undefined}>
      {/* One line from `sm`: the title takes at most 75% of the row and truncates with an ellipsis (full text
          on hover); the detail line takes the rest, right-aligned, and truncates too. The row never grows a
          second line. On a phone there is no room, so the detail may drop below. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 sm:flex-nowrap">
        <p className="min-w-0 truncate text-base font-normal leading-6 text-gray-900 first-letter:uppercase sm:max-w-[75%] dark:text-gray-100" title={title}>{title}</p>
        {metaText && (
          <p className="min-w-0 truncate text-[13px] text-gray-500 first-letter:uppercase sm:flex-1 sm:text-right dark:text-gray-400" title={metaText}>
            {parts
              ? parts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? ' · ' : null}
                  <span data-tone={part.tone || undefined} className={part.tone ? TONE_CLASS[part.tone] : undefined}>{part.text}</span>
                </React.Fragment>
              ))
              : meta}
          </p>
        )}
      </div>
    </SituationCard>
  );
}
